import { fi } from "zod/v4/locales";
import axiosInstance from "./axios";
import { Company, Organization } from "./models/companies.models";
import {
  CreateReportPayload,
  InternalReportStatusEnum,
  Report,
  ReportStatus,
  ReportType,
  UpdateReportPayload,
} from "./models/report.models";
import { ReportingCycleEnum } from "./models/reportingCycle.models";
import { AdminReportFilters } from "../types/ui-types";

const apiUrl = import.meta.env.VITE_BACKEND_API_URL;

const createReportQueryParams = ({
  organizationId,
  year,
  quarter,
  month,
  status,
  withRows,
}: {
  organizationId?: number | null;
  year?: number;
  quarter?: number;
  month?: number;
  status?: ReportStatus;
  withRows?: boolean;
}) => {
  const params = new URLSearchParams();

  if (organizationId) {
    params.append("organizationId", organizationId.toString());
  }
  if (year) {
    params.append("year", year.toString());
  }
  if (quarter) {
    params.append("quarter", quarter.toString());
  }
  if (month) {
    params.append("month", month.toString());
  }
  if (status) {
    params.append("status", status.toString());
  }

  params.append("withRows", String(withRows ?? false));

  return params.toString();
};

export const fetchAllReports = async (
  companyId: number,
  organizationId: number | null | undefined,
  bearerToken?: string,
  withRows?: boolean,
): Promise<Report[]> => {
  try {
    const queryString = createReportQueryParams({
      organizationId,
      withRows,
    });
    const url = `${apiUrl}/Report/reports/${companyId}?${queryString}`;

    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data.map((report: Report) => ({
      ...report,
      yearQuarterMonth: `${report.year ?? ""}-${report.quarter ?? ""}-${report.month ?? ""}`,
    }));
  } catch (error) {
    console.error("Failed to fetch reports:", error);
    throw error;
  }
};

export const fetchAllCompanyReports = async (
  companyId: number,
  bearerToken?: string,
  year?: number,
  quarter?: number,
  month?: number,
  reportStatus?: ReportStatus,
  withRows?: boolean,
): Promise<Report[]> => {
  try {
    const companyOrganizations = await axiosInstance.get(
      `${apiUrl}/Companies/${companyId}/organizations`,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );

    if (companyOrganizations.data.length === 0) {
      return fetchAllReports(companyId, null, bearerToken, withRows);
    }

    const companyReports = await Promise.all(
      companyOrganizations.data.map(async (organization: Organization) => {
        const queryString = createReportQueryParams({
          organizationId: organization.id,
          year,
          quarter,
          month,
          status: reportStatus,
          withRows,
        });
        const url = `${apiUrl}/Report/reports/${companyId}?${queryString}`;

        const response = await axiosInstance.get(url, {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
          },
        });

        return (response.data as Report[]).map((report) => ({
          ...report,
          organizationName: organization.organizationName,
          yearQuarterMonth: `${report.year ?? ""}-${report.quarter ?? ""}-${report.month ?? ""}`,
        }));
      }),
    );

    companyOrganizations.data = companyReports.flat();
    return companyOrganizations.data;
  } catch (error) {
    console.error("Failed to fetch company reports:", error);
    throw error;
  }
};

export const adminFetchCompanyReports = async (
  payload: {
    companyId: number;
    organizationId?: number;
    cycle?: ReportingCycleEnum;
    invoiceNumber?: string;
    skip?: number;
    take?: number;
  },
  bearerToken?: string,
): Promise<Report[]> => {
  try {
    const params = new URLSearchParams();

    if (!!payload.organizationId) {
      params.append("organizationId", payload.organizationId.toString());
    }
    if (!!payload.cycle) {
      params.append("cycle", payload.cycle.toString());
    }
    if (!!payload.invoiceNumber) {
      params.append("invoiceNumber", payload.invoiceNumber);
    }
    if (!!payload.skip) {
      params.append("skip", payload.skip.toString());
    }
    if (!!payload.take) {
      params.append("take", payload.take.toString());
    }

    const queryString = params.toString();
    const url = `${apiUrl}/Admin/reports/${payload.companyId}${queryString ? `?${queryString}` : ""}`;

    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch company reports:", error);
    throw error;
  }
};

export const adminFetchReports = async (
  filters: AdminReportFilters,
  bearerToken?: string,
): Promise<{
  data: {
    report: Report;
    company: Company;
    internalMessagesCount: number;
  }[];
  _count?: number;
}> => {
  try {
    const params = new URLSearchParams();

    if (filters.skip !== undefined) {
      params.append("skip", filters.skip.toString());
    }
    if (filters.take !== undefined) {
      params.append("take", filters.take.toString());
    }
    if (filters.company?.id) {
      params.append("companyId", filters.company.id.toString());
    }
    if (filters.organization?.id) {
      params.append("organizationId", filters.organization.id.toString());
    }
    if (filters.year) {
      params.append("year", filters.year.toString());
    }
    if (filters.quarter) {
      params.append("quarter", filters.quarter.toString());
    }
    if (filters.month) {
      params.append("month", filters.month.toString());
    }
    if (filters.requiresApproval) {
      params.append("requiresApproval", filters.requiresApproval.toString());
    }
    if (filters.reportStatus !== undefined) {
      params.append("reportStatus", filters.reportStatus.toString());
    }

    const queryString = params.toString();
    const url = `${apiUrl}/Admin/reports${queryString ? `?${queryString}` : ""}`;

    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });

    // Extract total count from response header
    const totalHeader =
      response.headers["total"] || response.headers["x-total-count"];
    const total = totalHeader ? parseInt(totalHeader, 10) : undefined;

    return {
      data: response.data,
      _count: total,
    };
  } catch (error) {
    console.error("Failed to fetch company reports:", error);
    throw error;
  }
};

export const fetchReportById = async (query: {
  companyId: number;
  organizationId?: number;
  bearerToken?: string;
  reportId: number;
}): Promise<Report> => {
  try {
    const url = `${apiUrl}/Report/reports/${query.companyId}/${query.reportId}${query.organizationId ? `?organizationId=${query.organizationId}` : ""}`;
    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${query.bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch report by ID:", error);
    throw error;
  }
};

export const adminFetchReportById = async (query: {
  bearerToken?: string;
  reportId: number;
}): Promise<Report> => {
  try {
    const url = `${apiUrl}/Admin/reports/report/${query.reportId}?includeComments=true`;
    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${query.bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch admin report by ID:", error);
    throw error;
  }
};

export const createReport = async (
  companyId: number,
  payload: CreateReportPayload,
  bearerToken?: string,
): Promise<Report> => {
  try {
    const url = payload?.organizationId
      ? `${apiUrl}/Report/reports/${companyId}${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`
      : `${apiUrl}/Report/reports/${companyId}`;
    const response = await axiosInstance.post(url, payload, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to create report:", error);
    throw error;
  }
};

export const updateReport = async (
  companyId: number,
  payload: UpdateReportPayload,
  bearerToken?: string,
): Promise<Report> => {
  try {
    const url = payload?.organizationId
      ? `${apiUrl}/Report/reports/${companyId}${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`
      : `${apiUrl}/Report/reports/${companyId}`;
    const response = await axiosInstance.put(url, payload, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to update report:", error);
    throw error;
  }
};

export const updateReportStatus = async (
  companyId: number,
  organizationId: number | undefined,
  payload: UpdateReportPayload,
  bearerToken?: string,
): Promise<Report> => {
  try {
    const url = `${apiUrl}/Report/reports/${companyId}${organizationId ? `?organizationId=${organizationId}` : ""}`;

    const response = await axiosInstance.put(
      url,
      {
        id: payload.id,
        companyId: payload.companyId,
        organizationId: payload.organizationId,
        year: payload.year,
        quarter: payload.quarter,
        month: payload.month,
        reportStatus: payload.reportStatus,
        reportType: payload.reportType,
        comment: payload.comment,
        rows: payload.rows,
      },
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update report:", error);
    throw error;
  }
};

export const adminChangeReportStatus = async (
  reportId: number,
  status: ReportStatus,
  bearerToken?: string,
): Promise<Report> => {
  try {
    const url = `${apiUrl}/Admin/reports/changestatus/${reportId}?newStatus=${status}`;
    const response = await axiosInstance.put(url, undefined, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to change report status:", error);
    throw error;
  }
};

export const adminChangeReportInternalStatus = async (
  reportId: number,
  internalStatus: InternalReportStatusEnum,
  bearerToken?: string,
): Promise<Report> => {
  try {
    const url = `${apiUrl}/Admin/reports/changeinternalstatus/${reportId}?internalStatus=${internalStatus}`;
    const response = await axiosInstance.put(url, undefined, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to change report internal status:", error);
    throw error;
  }
};

export const deleteCompanyReport = async (
  companyId: number,
  organizationId: number | undefined,
  reportId: number,
  bearerToken?: string,
): Promise<void> => {
  try {
    const url = `${apiUrl}/Report/reports/${companyId}/${reportId}${organizationId ? `?organizationId=${organizationId}` : ""}`;
    await axiosInstance.delete(url, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
  } catch (error) {
    console.error("Failed to delete report:", error);
    throw error;
  }
};
